import { ArrowLeftIcon } from '@heroicons/react/20/solid'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { useLazyQuery } from '@apollo/client'
import { useState, useEffect } from 'react'
import { GET_COUNTRY_LIST } from 'graphql/common/queries'
import loadingImage from '../../../assets/images/boat-loading.gif'
import { GET_UNIPAL_CONFIG } from 'graphql/unipal/queries'

export default function UnipalServiceDetails() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [unipalInfo, setunipalInfo] = useState(null)
    const [countries, setCountries] = useState([])
    const [effectiveFrom, setEffectiveFrom] = useState('2025-01-30')
    const [effectiveTo, setEffectiveTo] = useState('2028-01-30')

    const [datesLoaded, setDatesLoaded] = useState(false)

    const [getUnipalItemDetails, { loading: unipalLoading, error: unipalError, data }] = useLazyQuery(
        GET_UNIPAL_CONFIG,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (!datesLoaded && data.getUnipalConfigs.success && data.getUnipalConfigs.code === '200') {
                    const config = data.getUnipalConfigs.unipal_configs[0]

                    const from = toInputDate(config.effective_from)
                    const to = toInputDate(config.effective_to)

                    setunipalInfo(config)
                    setEffectiveFrom(from)
                    setEffectiveTo(to)
                    setDatesLoaded(true)
                    getUnipalItemDetails({
                        variables: {
                            filter: {
                                unipal_item_id: id,
                                effective_from: from,
                                effective_to: to,
                            },
                            page: {
                                offset: 0,
                                limit: 1,
                            },
                        },
                    })
                }
            },
        }
    )

    useEffect(() => {
        getUnipalItemDetails({
            variables: {
                filter: {
                    unipal_item_id: id,
                    effective_from: '2025-01-01',
                    effective_to: '2030-12-31',
                },
                page: {
                    offset: 0,
                    limit: 1,
                },
            },
        })
    }, [id])

    const [getCountryList] = useLazyQuery(GET_COUNTRY_LIST, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.country) {
                setCountries(data.country)
            }
        },
    })

    useEffect(() => {
        getCountryList({
            variables: { code: null },
        })
    }, [])

    const toInputDate = isoString => {
        return isoString ? isoString.split('T')[0] : ''
    }

    const getCountryFlag = countryCode => {
        return `https://flagcdn.com/w20/${countryCode?.toLowerCase()}.png`
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <div className="flex">
                    <button
                        onClick={() => {
                            navigate('/unipal-config')
                        }}
                        type="button"
                        className="inline-flex items-center gap-x-1 rounded-md text-sm text-gray-600"
                    >
                        <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-4 rtl:rotate-180" />
                        {t('Back')}
                    </button>
                    <h3 className="text-lg font-semibold leading-7 text-primary ps-2 pb-1">
                        {' '}
                        {t('Unipal Service Details')}
                    </h3>
                </div>
                <button
                    onClick={() => navigate(`/unipal-config/edit/${unipalInfo?.unipal_item_id}`)}
                    className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                >
                    {t('Edit Unipal Item')}
                </button>
            </div>
            {unipalLoading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
                </div>
            )}
            {unipalError && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('Failed to fetch unipal service details!')}</div>
                </div>
            )}

            {!unipalLoading && !unipalError && unipalInfo && (
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div className="max-sm:w-full sm:flex-1">
                        {/* Unipal Information */}
                        <div className="mt-5">
                            <div className="px-4 sm:px-0">
                                <h3 className="text-lg font-semibold leading-7 text-primary">
                                    {' '}
                                    {t('Item Information')}
                                </h3>
                            </div>

                            <div className="mt-3 border-t border-gray-100">
                                <dl className="divide-y divide-gray-100">
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">{t('User Id')}</dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {unipalInfo.unipal_item_id}
                                        </dd>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Item Type')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {unipalInfo.item_type}
                                        </dd>
                                    </div>
                                    {unipalInfo?.item_type !== 'TRIP' && (
                                        <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                {t('Daypass Name (English)')}
                                            </dt>
                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                {unipalInfo?.daypasses?.map(trip => (
                                                    <span
                                                        key={trip.daypass_id}
                                                        className="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded-md"
                                                    >
                                                        {trip.daypass_name?.en}
                                                    </span>
                                                ))}{' '}
                                            </dd>
                                        </div>
                                    )}
                                    {unipalInfo?.item_type !== 'TRIP' && (
                                        <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                {t('Daypass Name (Arabic)')}
                                            </dt>
                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                {unipalInfo?.daypasses?.map(trip => (
                                                    <span
                                                        key={trip.daypass_id}
                                                        className="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded-md"
                                                    >
                                                        {trip.daypass_name?.ar}
                                                    </span>
                                                ))}{' '}
                                            </dd>{' '}
                                        </div>
                                    )}
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">{t('Status')}</dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            <span
                                                className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                                                    unipalInfo.status_code === 'ACT'
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-red-100 text-red-800'
                                                }`}
                                            >
                                                {unipalInfo.status_code === 'ACT' ? t('Active') : t('Inactive')}
                                            </span>
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>

                        {/* Eligibility Information */}
                        <div className="mt-8">
                            <div className="px-4 sm:px-0">
                                <h3 className="text-lg font-semibold leading-7 text-primary">
                                    {t('Eligibility Information')}
                                </h3>
                            </div>
                            <div className="mt-3 border-t border-gray-100">
                                <dl className="divide-y divide-gray-100">
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">{t('Category')}</dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {unipalInfo.route_type}
                                        </dd>
                                    </div>
                                    {unipalInfo?.item_type !== 'DAYPASS' && (
                                        <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                {t('Routes')}
                                            </dt>
                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                <div className="flex flex-wrap gap-2">
                                                    {unipalInfo?.routes?.map(route => (
                                                        <span
                                                            key={route.route_id}
                                                            className="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded-md"
                                                        >
                                                            {route.route_name?.en}
                                                        </span>
                                                    ))}
                                                </div>{' '}
                                            </dd>
                                        </div>
                                    )}
                                    {unipalInfo?.item_type !== 'DAYPASS' && (
                                        <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                            <dt className="text-sm font-medium leading-6 text-gray-900">
                                                {t('Trips')}
                                            </dt>
                                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                <div className="flex flex-wrap gap-2">
                                                    {unipalInfo?.trips?.map(trip => (
                                                        <span
                                                            key={trip.trip_id}
                                                            className="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded-md"
                                                        >
                                                            {trip.trip_name?.en}
                                                        </span>
                                                    ))}
                                                </div>{' '}
                                            </dd>
                                        </div>
                                    )}
                                     <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Effective From')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {unipalInfo.effective_from}
                                        </dd>
                                    </div>
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Effective To')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {unipalInfo.effective_to}
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>
                        {/* User Audit Information */}
                        <div className="mt-8">
                            <div className="px-4 sm:px-0">
                                <h3 className="text-lg font-semibold leading-7 text-primary">
                                    {t('Audit Information')}
                                </h3>
                            </div>
                            <div className="mt-3 border-t border-gray-100">
                                <dl className="divide-y divide-gray-100">
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Created By')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {unipalInfo.user_audit.created_by || t('N/A')}
                                        </dd>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Created Date')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {unipalInfo.user_audit.created_date
                                                ? new Date(unipalInfo.user_audit.created_date).toLocaleString()
                                                : t('N/A')}
                                        </dd>
                                    </div>
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Modified By')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {unipalInfo.user_audit.modified_by || t('N/A')}
                                        </dd>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Modified Date')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {unipalInfo.user_audit.modified_date
                                                ? new Date(unipalInfo.user_audit.modified_date).toLocaleString()
                                                : t('N/A')}
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
